import { getAutoProps } from 'js/auto_props/client';
import type {
  PublishedArm,
  PublishedContent,
} from 'js/bulk_download/autotest/AutotestBulkDownload';

/**
 * The server-resolved autotest arm for this surface, or `undefined`.
 *
 * The whole experiment — arms, weights, and every arm's content — is a document
 * published to DynamoDB and resolved on the server
 * (`DynamicAutotestContent::Onsite`), so all this does is unwrap the auto-prop
 * the controller registered. `undefined` is a completely normal answer: no
 * document, killed surface, DynamoDB unreachable, or a page that does not
 * register `BulkDownloadFlags`. The modal then renders its compiled CONTROL.
 *
 * Its own module rather than a private helper in `show_bulk_download_modal`
 * only so it is reachable from jest: that entry point pulls in `Aedu`,
 * `renderModal` and the toast provider, and the cohort guarantee below is
 * exactly the kind of silent property that has to stay under test.
 *
 * Nothing here throws. `getAutoProps` throws when the auto-props script has not
 * rendered at all (mirroring `wonderchat_widget.ts`), and `contentJson` is
 * parsed defensively because a modal must not fail to open over a bad byte —
 * the envelope layer validates each slot properly on the way in
 * (`validateSlotValue`) and drops anything it does not like back to control.
 *
 * A BAD BYTE DROPS THE CONTENT, NEVER THE ENVELOPE. `heldOut` rides on the same
 * auto-prop as `contentJson`, so returning `undefined` on a parse failure threw
 * away the bucketing decision along with the unreadable content — and a
 * held-out visitor with no envelope is indistinguishable from an unbucketed
 * one, so they render the shipped modal while `user_tests` records them in the
 * holdout. That is the silent contamination `AutotestBulkDownload`'s `heldOut`
 * docstring warns about, and it is unrecoverable in a since-launch series.
 *
 * So the fallback is `content: {}` — every arm's slots fall through to the
 * compiled bag, which is what an unparseable document should render anyway,
 * while the cohort this visitor belongs to survives. Only a missing auto-prop
 * (genuinely "no document is being served") still yields `undefined`.
 */
export function publishedArm(): PublishedArm | undefined {
  try {
    const autotest = getAutoProps('BulkDownloadFlags')?.autotest;
    if (!autotest) return undefined;
    const envelope = {
      testKey: autotest.testKey,
      arm: autotest.arm,
      bucket: autotest.bucket,
      heldOut: autotest.heldOut,
      // Spread-only-when-true so an organic assignment's shape is unchanged.
      // A review-override impression must not carry the wave's analytics
      // identity — see PublishedArm#preview.
      ...(autotest.preview === true && { preview: true as const }),
    };
    try {
      const parsed: unknown = JSON.parse(autotest.contentJson);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return { ...envelope, content: {} };
      }
      return { ...envelope, content: parsed as PublishedContent };
    } catch {
      return { ...envelope, content: {} };
    }
  } catch {
    return undefined;
  }
}
